import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error";
import Table, { TableSize } from "@via/components/table/table";
import { ERROR } from "@via/utils/constants";
import { downloadCSV, formatDateString } from "@via/utils/helpers";
import { useGetOrganizationChargeHistory } from "@via/pages/account/admin/organizations/organizations.api.tsx";
import { GetChargeHistoryResponseBodyItem } from "common";

const columnHelper = createColumnHelper<GetChargeHistoryResponseBodyItem>();

const OrganizationCost = () => {
  const {
    data: organizationChargeHistory,
    isLoading: isOrganizationChartHistoryLoading,
    isError: isOrganizationChartHistoryError,
  } = useGetOrganizationChargeHistory();

  const columns = [
    columnHelper.accessor("id", {
      cell: info => info.getValue(),
      header: () => <span>Id</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("organizationId", {
      cell: info => info.getValue(),
      header: () => <span>Organization Id</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("cloudVendor", {
      cell: info => info.getValue(),
      header: () => <span>Cloud Vendor</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("billingPeriodStart", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LL")}</span>;
      },
      header: () => <span>Period Start</span>,
      enableSorting: true,
    }),
    columnHelper.accessor("billingPeriodEnd", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LL")}</span>;
      },
      header: () => <span>Period End</span>,
      enableSorting: true,
    }),
    columnHelper.accessor("serviceCosts.networking", {
      cell: info => info.getValue(),
      header: () => <span>Networking Cost</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("serviceCosts.cloud_storage", {
      cell: info => info.getValue(),
      header: () => <span>Cloud Storage Cost</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("serviceCosts.compute_engine", {
      cell: info => info.getValue(),
      header: () => <span>Compute Engine Cost</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("serviceCosts.miscellaneous", {
      cell: info => info.getValue(),
      header: () => <span>Miscellaneous Cost</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("totalCloudCost", {
      cell: info => info.getValue(),
      header: () => <span>Total Cloud Cost</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("marginPercentage", {
      cell: info => {
        const val = info.getValue();
        return <span>{val.toFixed(2)}%</span>;
      },
      header: () => <span>Margin Percentage</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("marginAmount", {
      cell: info => info.getValue(),
      header: () => <span>Margin Amount</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("finalCost", {
      cell: info => info.getValue(),
      header: () => <span>Final Cost</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("paymentProvider", {
      cell: info => info.getValue(),
      header: () => <span>Payment Provider</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("chargedAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LL")}</span>;
      },
      header: () => <span>Charged At</span>,
      enableSorting: true,
    }),
  ] as Array<ColumnDef<GetChargeHistoryResponseBodyItem>>;

  const handleExportCostData = () => {
    downloadCSV(columns, organizationChargeHistory || []);
  };

  return (
    <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">Organization Cost</h2>
        <button
          disabled={!organizationChargeHistory?.length}
          onClick={handleExportCostData}
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          Export Cost Data
        </button>
      </div>

      {isOrganizationChartHistoryError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <div className="overflow-auto">
          <Table
            data={organizationChargeHistory || []}
            columns={columns}
            isLoading={isOrganizationChartHistoryLoading}
            total={(organizationChargeHistory || []).length}
            size={TableSize.Big}
            customTableClassName="w-max whitespace-nowrap"
            applyColumnSize
            initialSort={[{ id: "chargedAt", desc: true }]}
          />
        </div>
      )}
    </div>
  );
};

export default OrganizationCost;
